import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CloudUpload, HardDrive } from 'lucide-react';
import { useT } from '@/i18n';
import { ErrorBox, Ltr, Modal, Spinner } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { useWs } from '@/lib/api/hooks';
import { can } from '@/lib/constants';
import { formatBytes } from '@/lib/format';
import { fingerprint, newRecord, saveRecord } from '@/lib/upload/plan';
import { logError, logInfo } from '@/lib/log';
import { useMembership } from '@/store/session';
import { useUploads } from '@/store/uploads';
import type { CloudUploadPlan, PipelineSession } from '@/lib/api/types';

/** Match chosen files to the pending uploads: same name and size first, then a unique size. */
export function matchFiles(plans: CloudUploadPlan[], files: File[]): Map<string, File> {
  const out = new Map<string, File>();
  const left = [...files];
  for (const p of plans) {
    const i = left.findIndex((f) => f.name === p.filename && f.size === p.size);
    if (i >= 0) out.set(p.source_id, left.splice(i, 1)[0]);
  }
  for (const p of plans) {
    if (out.has(p.source_id)) continue;
    const same = left.filter((f) => f.size === p.size);
    if (same.length === 1) {
      out.set(p.source_id, same[0]);
      left.splice(left.indexOf(same[0]), 1);
    }
  }
  return out;
}

/**
 * "Process in the cloud": the local sources become multipart uploads on the server; each chosen file is
 * registered in the resumable-upload ledger under its server upload id, so the normal upload queue continues
 * exactly those uploads. The upload page then starts processing in the cloud.
 */
function CloudUploadDialog({ session, episodePath, onClose }: { session: PipelineSession; episodePath: string; onClose: () => void }) {
  const { t, td } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const w = useWs();
  const [plans, setPlans] = useState<CloudUploadPlan[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [chosen, setChosen] = useState<Map<string, File>>(new Map());
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let live = true;
    api.pipeline
      .processInCloud(session.id)
      .then((r) => {
        if (!live) return;
        logInfo('pipeline', `process in the cloud session=${session.id} uploads=${r.uploads.length}`);
        setPlans(r.uploads);
        void qc.invalidateQueries({ queryKey: qk.session(w, session.id) });
      })
      .catch((e) => {
        logError('pipeline', `process in the cloud failed session=${session.id}`, e);
        if (live) setError(e);
      });
    return () => {
      live = false;
    };
  }, [session.id, qc, w]);

  const pick = (list: FileList | null) => {
    if (!list || !plans) return;
    const next = new Map(chosen);
    for (const [k, v] of matchFiles(plans.filter((p) => !next.has(p.source_id)), [...list])) next.set(k, v);
    setChosen(next);
  };

  const start = () => {
    if (!plans) return;
    const files: File[] = [];
    for (const p of plans) {
      const f = chosen.get(p.source_id);
      if (!f) continue;
      saveRecord(fingerprint(session.id, f), newRecord(session.id, f, p.kind, { source_id: p.source_id, upload_id: p.upload_id, part_size: p.part_size, part_count: p.part_count, part_urls: [] }));
      files.push(f);
    }
    const store = useUploads.getState();
    store.add(session.id, files);
    store.startAll(session.id);
    logInfo('upload', `cloud fallback uploads queued session=${session.id} files=${files.length}`);
    navigate(`${episodePath}/upload`);
  };

  const matched = plans ? plans.filter((p) => chosen.has(p.source_id)).length : 0;
  return (
    <Modal
      title={t('cloudUploadTitle')}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={!plans || matched === 0} onClick={start}>{t('cloudUploadStart')}</button>
        </>
      }
    >
      {error ? <ErrorBox error={error} /> : !plans ? <Spinner label={t('loading')} /> : (
        <div className="stack" style={{ gap: 12 }}>
          <span className="muted small">{t('cloudUploadSub')}</span>
          <div className="row" style={{ gap: 10 }}>
            <button type="button" className="btn" onClick={() => input.current?.click()}>{t('cloudUploadPick')}</button>
            <span className="small" aria-live="polite">{t('cloudUploadMatched', { n: matched, total: plans.length })}</span>
            <input ref={input} type="file" multiple hidden data-testid="cloud-file-input" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
          </div>
          <div className="card" style={{ overflow: 'hidden' }}>
            {plans.map((p) => {
              const f = chosen.get(p.source_id);
              return (
                <div key={p.source_id} className="card-row row" style={{ padding: '8px 12px', gap: 10 }}>
                  {f ? <Check size={14} aria-hidden style={{ color: 'var(--green)' }} /> : <HardDrive size={14} aria-hidden style={{ color: 'var(--muted)' }} />}
                  <Ltr className="mono ellipsis"><span style={{ fontSize: 12 }}>{p.filename}</span></Ltr>
                  <span className="tag">{td(`kind_${p.kind}`)}</span>
                  <span className="muted small push">{formatBytes(p.size)} · {f ? t('uploadWaiting') : t('cloudUploadMissing')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Where a session with files on a studio computer runs (amendment 14): "Processing on Studio Mac", and, when that
 * computer has been unreachable too long (or was revoked), the offer to upload the recordings and process in the cloud.
 */
export function DeviceRouting({ session, episodePath }: { session: PipelineSession; episodePath: string }) {
  const { t } = useT();
  const membership = useMembership();
  const [open, setOpen] = useState(false);
  const r = session.routing;
  if (!r || !r.device) return null;
  const mayUpload = can(membership?.role, 'videos.edit');
  const pendingCloud = r.cloud_fallback && session.status === 'uploading' && session.sources.some((s) => !s.uploaded);
  const busy = session.status === 'queued' || session.status === 'processing';
  return (
    <>
      {r.needs_upload || pendingCloud ? (
        <div className="alert alert-warn row wrap" role="status" style={{ gap: 12 }}>
          <span className="stack" style={{ gap: 2, flex: 1, minWidth: 240 }}>
            {pendingCloud ? (
              <strong>{t('cloudUploadTitle')}</strong>
            ) : r.device.status === 'revoked' ? (
              <span>{t('needsUploadRevoked')}</span>
            ) : (
              <>
                <strong>{t('needsUploadTitle', { name: r.device.name })}</strong>
                <span className="small">{t('needsUploadBody')}</span>
              </>
            )}
          </span>
          {mayUpload ? (
            <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}><CloudUpload size={14} aria-hidden />{pendingCloud ? t('cloudUploadPick') : t('processInCloud')}</button>
          ) : null}
        </div>
      ) : busy && r.target === 'device' ? (
        <div className="row muted small" role="status" style={{ gap: 6 }}>
          <HardDrive size={13} aria-hidden />
          {r.device.status === 'online' ? t('processingOnDevice', { name: r.device.name }) : t('processingWaitingDevice', { name: r.device.name })}
        </div>
      ) : null}
      {open ? <CloudUploadDialog session={session} episodePath={episodePath} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
